/* eslint-disable @angular-eslint/prefer-inject --
 * Constructor parameter injection is intentional: it lets specs instantiate
 * this service directly with stubs and skip Angular's TestBed boot.
 */
import { Injectable } from '@angular/core';
import { SpanStatusCode } from '@opentelemetry/api';

import { AppBootstrapTracker } from './app-bootstrap.tracker';
import { MetricsRouterTracker } from './metrics-router.tracker';
import { SPA_OTEL_ATTR, SPA_PERF_SPAN_PREFIX, SpaPerformanceEventType } from './metrics.types';
import { getMetricsTracer } from './otel-web.bootstrap';

export interface RecordPageLoadInput {
  tenantId: string;
  pageName: string;
  durationMs: number;
  occurredAt?: Date;
  sourceEventId?: string;
}

/**
 * Public surface for SPA metric emission.
 *
 * Phase 2D wiring: `init()` enables the router + bootstrap trackers. Most
 * feature code never calls this service directly — page-load coverage is
 * automatic via the router subscriber. `recordPageLoad` is provided as an
 * escape hatch for code paths the router-based instrumentation can't see
 * (e.g. iframe-hosted views, manual reload screens).
 *
 * All public methods are non-throwing: on any failure, they swallow the error
 * so a metrics issue can never break a page render.
 */
@Injectable({ providedIn: 'root' })
export class MetricsService {
  private started = false;

  constructor(
    private readonly router: MetricsRouterTracker,
    private readonly bootstrap: AppBootstrapTracker
  ) {}

  public init(): void {
    if (this.started) {
      return;
    }
    this.started = true;
    try {
      this.bootstrap.start();
      this.router.start();
    } catch {
      // Defensive: never let metrics initialization break the app.
    }
  }

  public stop(): void {
    if (!this.started) {
      return;
    }
    this.started = false;
    try {
      this.router.stop();
    } catch {
      // ignore
    }
  }

  public recordPageLoad(input: RecordPageLoadInput): void {
    try {
      const tracer = getMetricsTracer();
      const startTime = input.occurredAt ?? new Date();
      const span = tracer.startSpan(`${SPA_PERF_SPAN_PREFIX}page_load`, {
        startTime,
        attributes: {
          [SPA_OTEL_ATTR.TENANT_ID]: input.tenantId,
          [SPA_OTEL_ATTR.EVENT_TYPE]: SpaPerformanceEventType.PAGE_LOAD,
          [SPA_OTEL_ATTR.PAGE_NAME]: input.pageName,
          [SPA_OTEL_ATTR.DURATION_SECONDS]: input.durationMs / 1000,
          ...(input.sourceEventId ? { [SPA_OTEL_ATTR.SOURCE_EVENT_ID]: input.sourceEventId } : {}),
        },
      });
      span.setStatus({ code: SpanStatusCode.OK });
      span.end(startTime);
    } catch {
      // ignore
    }
  }
}
